import { useEffect, useMemo, useState } from 'react';
import { first } from 'lodash';
import { machineListCache } from 'App/Activity/MachineStateManager';
import { MachineRegistrationState, PowerState } from 'App/Activity/Network/ActionsUtil';
import {
	Machine,
	MachineData,
	MachineError,
	Session,
	useResourceManagerContext,
} from 'App/Activity/ResourceManagerContext';
import { isStaleMachineData } from 'App/Activity/Utility';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { useFeatureCanary } from 'utils/useFeatureCanary';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';

const SUPPORTED_POWER_STATES = new Set([
	PowerState.UNKNOWN,
	PowerState.UNMANAGED,
	PowerState.UNAVAILABLE,
	PowerState.OFF,
	PowerState.ON,
	PowerState.SUSPENDED,
	PowerState.TURNING_ON,
	PowerState.TURNING_OFF,
	PowerState.SUSPENDING,
	PowerState.RESUMING,
	PowerState.NOT_SUPPORTED,
	PowerState.VIRTUAL_MACHINE_NOT_FOUND,
	PowerState.LOADING,
	PowerState.STARTING,
	PowerState.READY,
]);

const getLatestMachineData = (
	machineDataFromMachineApi: MachineData,
	machineDataFromSessionApi: MachineData
) => {
	let validMachineData = machineDataFromMachineApi;

	if (
		!validMachineData ||
		(machineDataFromSessionApi?.lastUpdatedTime &&
			machineDataFromMachineApi?.lastUpdatedTime <
				machineDataFromSessionApi.lastUpdatedTime)
	) {
		validMachineData = machineDataFromSessionApi;
	}

	if (isStaleMachineData(validMachineData?.lastUpdatedTime)) {
		validMachineData = null;
	}
	return validMachineData;
};

const getResourceState = (
	resource: Resource,
	machines: (Machine | MachineError)[],
	sessions: Session[],
	isReadyPowerStateEnabled: boolean
) => {
	const machineDataFromMachineApi = (
		machines?.find(({ resourceId }) => resourceId === resource.id) as Machine
	)?.machineData;
	const machineDataFromSessionApi = sessions.find(
		session => first(session.applications)?.resource?.id === resource.id
	)?.machineData;

	const latestMachineData = getLatestMachineData(
		machineDataFromMachineApi,
		machineDataFromSessionApi
	);

	let powerState = latestMachineData?.powerState || PowerState.UNKNOWN;
	if (
		isReadyPowerStateEnabled &&
		powerState === PowerState.ON &&
		latestMachineData?.registrationState === MachineRegistrationState.REGISTERED
	) {
		powerState = PowerState.READY;
	}

	return {
		powerState,
		latestTimeStamp: latestMachineData?.lastUpdatedTime || null,
	};
};

export const useResourceState = (resource: Resource) => {
	const context = useResourceManagerContext();
	const [state, setState] = useState<PowerState>(PowerState.NOT_SUPPORTED);
	const [lastUpdatedTime, setLastUpdatedTime] = useState<string | null>(null);
	const isMachinePowerStateEnabled = useFeatureCanary(
		FeatureFlag.EnableMachinePowerState
	);
	const isReadyPowerStateEnabled = useFeatureCanary(FeatureFlag.EnableReadyPowerState);
	const isDisconnectAndLogoutEnabled = useFeatureCanary(
		FeatureFlag.EnableDisconnectAndLogout
	);

	const isLoading = useMemo(() => {
		return (
			context?.machineStatus?.loading ||
			context?.localSessions?.loading ||
			context?.remoteSessions?.loading ||
			context?.hibernatedSessions?.loading
		);
	}, [
		context?.machineStatus?.loading,
		context?.localSessions?.loading,
		context?.remoteSessions?.loading,
		context?.hibernatedSessions?.loading,
	]);

	const allSessions = useMemo(
		() => [
			...(context?.localSessions?.sessions || []),
			...(context?.remoteSessions?.sessions || []),
			...(context?.hibernatedSessions?.sessions || []),
		],
		[
			context?.localSessions?.sessions,
			context?.remoteSessions?.sessions,
			context?.hibernatedSessions?.sessions,
		]
	);

	useEffect(() => {
		let newState: PowerState;
		let newLastUpdatedTime: string | null = null;

		if (!resource?.isdesktop) {
			newState = PowerState.NOT_SUPPORTED;
		} else if (!isMachinePowerStateEnabled && !isDisconnectAndLogoutEnabled) {
			newState = PowerState.NOT_SUPPORTED;
		} else if (isLoading) {
			newState = PowerState.LOADING;
		} else {
			const machineData = getResourceState(
				resource,
				machineListCache.getAllMachines()?.machines || [],
				allSessions,
				isReadyPowerStateEnabled
			);

			if (resource.canquerymachinestate && isMachinePowerStateEnabled) {
				newState = machineData?.powerState || PowerState.UNKNOWN;
				newLastUpdatedTime = machineData?.latestTimeStamp || null;
			} else {
				newState = PowerState.NOT_SUPPORTED;
			}
		}

		if (!SUPPORTED_POWER_STATES.has(newState)) {
			newState = PowerState.NOT_SUPPORTED;
		}

		setState(newState);
		setLastUpdatedTime(newLastUpdatedTime);
	}, [
		isLoading,
		allSessions,
		resource,
		isMachinePowerStateEnabled,
		isReadyPowerStateEnabled,
		isDisconnectAndLogoutEnabled,
	]);

	return { state, lastUpdatedTime };
};
